<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Users
      </button>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-person-plus me-2"></i>
              Create New User & Send Invitation
            </h5>
          </div>
          <div class="card-body">
            <!-- Error Message -->
            <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <form [formGroup]="createForm" (ngSubmit)="onSubmit()">
              <!-- Basic Information -->
              <div class="border-bottom pb-2 mb-3">
                <h6 class="fw-semibold mb-1">Basic Information</h6>
                <p class="text-muted small mb-0">User's personal details</p>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="firstName" class="form-label">First Name <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="firstName"
                    formControlName="firstName"
                    placeholder="John"
                    [class.is-invalid]="createForm.get('firstName')?.invalid && createForm.get('firstName')?.touched"
                  />
                  <div *ngIf="createForm.get('firstName')?.invalid && createForm.get('firstName')?.touched" class="invalid-feedback">
                    First name is required
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="lastName" class="form-label">Last Name <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="lastName"
                    formControlName="lastName"
                    placeholder="Doe"
                    [class.is-invalid]="createForm.get('lastName')?.invalid && createForm.get('lastName')?.touched"
                  />
                  <div *ngIf="createForm.get('lastName')?.invalid && createForm.get('lastName')?.touched" class="invalid-feedback">
                    Last name is required
                  </div>
                </div>

                <div class="col-12 mb-4">
                  <label for="email" class="form-label">Email Address <span class="text-danger">*</span></label>
                  <input
                    type="email"
                    class="form-control"
                    id="email"
                    formControlName="email"
                    placeholder="john.doe@example.com"
                    [class.is-invalid]="createForm.get('email')?.invalid && createForm.get('email')?.touched"
                  />
                  <div *ngIf="createForm.get('email')?.invalid && createForm.get('email')?.touched" class="invalid-feedback">
                    Valid email is required
                  </div>
                  <div class="form-text">
                    User will receive an invitation email at this address
                  </div>
                </div>
              </div>

              <!-- Organization -->
              <div class="border-bottom pb-2 mb-3">
                <h6 class="fw-semibold mb-1">Organization</h6>
                <p class="text-muted small mb-0">Create a new organization or add to an existing one</p>
              </div>

              <!-- Organization Mode Toggle -->
              <div class="btn-group w-100 mb-3" role="group" aria-label="Organization mode">
                <input
                  type="radio"
                  class="btn-check"
                  name="orgMode"
                  id="orgModeNew"
                  autocomplete="off"
                  [checked]="organizationMode === 'new'"
                  (change)="setOrganizationMode('new')"
                />
                <label class="btn btn-outline-primary" for="orgModeNew">
                  <i class="bi bi-building-add me-2"></i>
                  Create New Organization
                </label>

                <input
                  type="radio"
                  class="btn-check"
                  name="orgMode"
                  id="orgModeExisting"
                  autocomplete="off"
                  [checked]="organizationMode === 'existing'"
                  (change)="setOrganizationMode('existing')"
                />
                <label class="btn btn-outline-primary" for="orgModeExisting">
                  <i class="bi bi-building me-2"></i>
                  Add to Existing Organization
                </label>
              </div>

              <!-- New Organization Fields -->
              <div *ngIf="organizationMode === 'new'" class="card border-0 mb-4">
                <div class="card-body">
                  <div class="row">
                    <div class="col-12 mb-3">
                      <label for="companyName" class="form-label">
                        Organization Name <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="companyName"
                        formControlName="companyName"
                        placeholder="Enter organization name"
                        [class.is-invalid]="createForm.get('sendInvitation')?.value && !createForm.get('companyName')?.value?.trim() && createForm.get('companyName')?.touched"
                      />
                      <div *ngIf="createForm.get('sendInvitation')?.value && !createForm.get('companyName')?.value?.trim() && createForm.get('companyName')?.touched" class="invalid-feedback">
                        Organization name is required
                      </div>
                    </div>
                  </div>
                  <div class="alert alert-info mb-0 py-2">
                    <i class="bi bi-info-circle me-2"></i>
                    This user will be added as the <strong>Owner</strong> of the new organization.
                  </div>
                </div>
              </div>

              <!-- Existing Organization Fields -->
              <div *ngIf="organizationMode === 'existing'" class="card border-0 mb-4">
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label for="organizationId" class="form-label">
                        Select Organization <span class="text-danger">*</span>
                      </label>
                      <select
                        class="form-select"
                        id="organizationId"
                        formControlName="organizationId"
                        [disabled]="loadingOrganizations"
                        [class.is-invalid]="createForm.get('sendInvitation')?.value && !createForm.get('organizationId')?.value && createForm.get('organizationId')?.touched"
                      >
                        <option value="">Select an organization...</option>
                        <option *ngFor="let org of organizations" [value]="org.id">
                          {{ org.name }}
                        </option>
                      </select>
                      <div *ngIf="loadingOrganizations" class="form-text">
                        <span class="spinner-border spinner-border-sm me-2"></span>
                        Loading organizations...
                      </div>
                      <div *ngIf="createForm.get('sendInvitation')?.value && !createForm.get('organizationId')?.value && createForm.get('organizationId')?.touched" class="invalid-feedback">
                        Please select an organization
                      </div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label for="invitationType" class="form-label">User Role <span class="text-danger">*</span></label>
                      <select
                        class="form-select"
                        id="invitationType"
                        formControlName="invitationType"
                        [class.is-invalid]="createForm.get('invitationType')?.invalid && createForm.get('invitationType')?.touched"
                      >
                        <option *ngFor="let type of invitationTypes" [value]="type.value">
                          {{ type.label }}
                        </option>
                      </select>
                      <div class="form-text">
                        Select the user's role within the organization
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Invitation Options -->
              <div class="border-bottom pb-2 mb-3">
                <h6 class="fw-semibold mb-1">Invitation Options</h6>
              </div>

              <div class="form-check mb-4">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="sendInvitation"
                  formControlName="sendInvitation"
                />
                <label class="form-check-label" for="sendInvitation">
                  <strong>Send invitation email immediately</strong>
                  <div class="text-muted small mt-1">
                    User will receive an email with a link to complete their account setup and create a password
                  </div>
                </label>
              </div>

              <!-- Notice -->
              <div class="alert alert-info mb-4">
                <p class="mb-2">
                  <i class="bi bi-info-circle-fill me-2"></i>
                  <strong>How This Works:</strong>
                </p>
                <ul class="mb-0 small text-body-secondary">
                  <li>A user profile will be created with the information above</li>
                  <li>An invitation will be sent to the user's email address</li>
                  <li>The user will click the invitation link and create their password</li>
                  <li>Once the password is created, their Firebase account will be linked</li>
                  <li>The user can then log in and complete their onboarding checklist</li>
                </ul>
              </div>

              <!-- Actions -->
              <div class="d-flex gap-2">
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="loading || createForm.invalid"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="bi bi-envelope-plus me-2"></i>
                  {{ loading ? 'Creating User...' : 'Create User & Send Invitation' }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="cancel()"
                  [disabled]="loading"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
